import React, { useState } from 'react';
import { saveUserProfile, isUsernameTaken } from '../../core/utils/saveUserProfile';
import { useAuth } from '../../core/contexts/AuthContext';
import { validateUsernameContent } from '../../core/utils/usernameModeration';
import './AuthModal.css';

/**
 * ProfileSetup Component
 * Shown when a user has Firebase Auth account but no Firestore profile
 * This handles edge cases like:
 * - Users created before Firestore integration
 * - Profile save failures during signup
 * - Manual user creation
 */
export default function ProfileSetup({ onProfileCreated }) {
  const { currentUser } = useAuth();
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate username
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    // Check username for inappropriate content and reserved names
    const usernameValidation = validateUsernameContent(username);
    if (!usernameValidation.isValid) {
      setError(usernameValidation.reason);
      return;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    if (username.length > 20) {
      setError('Username must be less than 20 characters');
      return;
    }

    // Only allow alphanumeric and underscores
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setLoading(true);

    try {
      // Check if username is taken
      const taken = await isUsernameTaken(username);
      if (taken) {
        setError('Username is already taken. Please choose another.');
        setLoading(false);
        return;
      }

      // Create profile in Firestore
      await saveUserProfile(currentUser.uid, currentUser.email, username);

      // Notify parent component that profile was created
      if (onProfileCreated) {
        onProfileCreated();
      }
    } catch (err) {
      setError(err.message || 'Failed to create profile. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal">
        <div className="auth-header">
          <h2>Complete Your Profile</h2>
          <p className="auth-subtitle">
            Welcome back! We need to set up your profile.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="auth-error">{error}</div>}

          <div className="auth-form-group">
            <label htmlFor="username">Choose a Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              disabled={loading}
              autoFocus
            />
            <small className="auth-hint">
              3-20 characters, letters, numbers, and underscores only
            </small>
          </div>

          <div className="auth-form-group">
            <label>Email</label>
            <input
              type="text"
              value={currentUser?.email || ''}
              disabled
              className="auth-input-disabled"
            />
            <small className="auth-hint">Your email from Firebase Auth</small>
          </div>

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={loading}
          >
            {loading ? 'Creating Profile...' : 'Create Profile'}
          </button>
        </form>

        <div className="auth-footer">
          <p className="auth-note">
            Your username will be displayed on the leaderboard and throughout
            the platform. Choose wisely!
          </p>
        </div>
      </div>
    </div>
  );
}
